import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification } from "antd";
import { apiRequest } from "util/services";
import {
  buildKpiGroups,
  pivotFilterFromPivots,
  resolveKpiTitle,
  sortSourcesByLastUpdate,
  formatKpiCreateSubtopicNotice,
} from "./kpiViewUtils";
import { isKpiPaymentRequired } from "util/alertLimits";

/** Matches one KPI row (4 cards). API paginates as start_index * batch_size. */
const KPI_PAGE_BATCH = 4;
export const KPI_VIEW_MAX_DEFAULT = 25;

export default function useKpiBoard({
  selectedTeamItem,
  chartPeriod,
  setDataLoading,
  t,
  startIndex = 0,
  numTopKpis = KPI_PAGE_BATCH,
  loadSources = true,
  showReviews = false,
  progressiveLoad = false,
  onOrgAddonUpgradeRequired,
}) {
  const [loading, setLoading] = useState(Boolean(selectedTeamItem));
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [pagesSubtitle, setPagesSubtitle] = useState([]);
  const [totalKpis, setTotalKpis] = useState(0);
  const [kpiOrgUsed, setKpiOrgUsed] = useState(0);
  const [kpiMaxLimit, setKpiMaxLimit] = useState(KPI_VIEW_MAX_DEFAULT);
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const fetchRequestId = useRef(0);

  const fetchBoard = useCallback(async () => {
    if (!selectedTeamItem) return;
    const requestId = ++fetchRequestId.current;
    setLoading(true);
    setBackgroundLoading(false);
    if (setDataLoading) setDataLoading(true);

    const basePayload = {
      team: String(selectedTeamItem),
      period: chartPeriod === "M" || !chartPeriod
        ? "30D"
        : chartPeriod === "7D"
          ? "W"
          : chartPeriod,
      show_reviews: Boolean(showReviews),
    };

    const fetchPage = (page, batchSize) =>
      apiRequest(
        "executive_summary/v2/top_data",
        "POST",
        {
          ...basePayload,
          start_index: page,
          num_top_kpis: batchSize,
        },
        true
      );

    const batchSize = progressiveLoad ? KPI_PAGE_BATCH : numTopKpis;
    const firstPage = progressiveLoad ? 0 : startIndex;
    const result = await fetchPage(firstPage, batchSize);

    if (requestId !== fetchRequestId.current) return;

    if (!result?.success) {
      notification.error({
        message: result?.error || t("team_kpi_view.fetch_kpis_failed", "Could not load KPI data"),
      });
      setLoading(false);
      if (setDataLoading) setDataLoading(false);
      return;
    }

    const firstBatch = result.success.top || [];
    const total = result.success.total_top_metrics || 0;
    setKpis(firstBatch);
    setPagesSubtitle(result.success.pages_subtitle || []);
    setTotalKpis(total);
    setKpiOrgUsed(result.success.kpi_used ?? 0);
    setKpiMaxLimit(result.success.kpi_max_limit || KPI_VIEW_MAX_DEFAULT);
    setLoading(false);
    if (setDataLoading) setDataLoading(false);

    if (!progressiveLoad || total <= firstBatch.length) {
      return;
    }

    setBackgroundLoading(true);
    let merged = [...firstBatch];
    let page = 1;
    while (merged.length < total) {
      const more = await fetchPage(page, KPI_PAGE_BATCH);
      if (requestId !== fetchRequestId.current) return;

      const chunk = more?.success?.top || [];
      if (!chunk.length) break;

      merged = [...merged, ...chunk];
      setKpis(merged);
      page += 1;
    }
    if (requestId === fetchRequestId.current) {
      setBackgroundLoading(false);
    }
  }, [
    selectedTeamItem,
    chartPeriod,
    setDataLoading,
    startIndex,
    numTopKpis,
    showReviews,
    progressiveLoad,
  ]);

  const fetchSources = useCallback(async () => {
    setSourcesLoading(true);
    const sourceResult = await apiRequest(
      "executive_summary/source",
      "POST",
      true
    );
    const sourceList = sortSourcesByLastUpdate(sourceResult?.success || []);
    setSources(sourceList);
    setSourcesLoading(false);
  }, []);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    if (loadSources) fetchSources();
  }, [fetchSources, loadSources]);

  const groups = useMemo(
    () => buildKpiGroups(kpis, pagesSubtitle),
    [kpis, pagesSubtitle]
  );

  const kpiLimitReached = kpiOrgUsed >= kpiMaxLimit;
  const remainingKpiSlots = Math.max(0, kpiMaxLimit - kpiOrgUsed);

  const addKpi = useCallback(
    async ({ metric, dashboardIds, pivots = [] }) => {
      const metrics = Array.isArray(metric) ? metric : [metric];
      if (!selectedTeamItem || metrics.length === 0 || !dashboardIds?.length) {
        return false;
      }
      if (kpiLimitReached) {
        notification.error({
          message: t("team_kpi_view.86", { max: kpiMaxLimit }),
        });
        return false;
      }
      setLoading(true);
      const payload = {
        dashboard_ids: dashboardIds.map((id) => parseInt(id, 10)),
        metric: metrics,
        bottom_metric: null,
        block_location: "Top",
        operation: "sum",
        team: String(selectedTeamItem),
        custom_title: "",
        ai_bubbles: [],
      };
      if (Array.isArray(pivots) && pivots.length > 0) {
        payload.pivots = pivots;
        const pivotFilter = pivotFilterFromPivots(pivots);
        if (pivotFilter) payload.pivot_filter = pivotFilter;
      }
      const result = await apiRequest(
        "executive_summary/v2/chartData",
        "POST",
        payload,
        true
      );
      if (result?.success && !result?.error) {
        const subtopicNotice = formatKpiCreateSubtopicNotice(result, t);
        notification.success({
          message: t("team_kpi_view.40"),
          description: subtopicNotice || undefined,
        });
        await fetchBoard();
        return true;
      }
      if (isKpiPaymentRequired(result) && onOrgAddonUpgradeRequired) {
        onOrgAddonUpgradeRequired();
      }
      notification.error({ message: result?.error || t("team_kpi_view.add_kpi_failed", "Could not add KPI") });
      setLoading(false);
      return false;
    },
    [selectedTeamItem, fetchBoard, t, kpiLimitReached, kpiMaxLimit, onOrgAddonUpgradeRequired]
  );

  const createKpisFromTopics = useCallback(
    async ({ topics, dashboardIds, pivots = [], mode = "combined" }) => {
      const topicList = (Array.isArray(topics) ? topics : [topics]).filter(
        Boolean
      );
      const hasPivots = Array.isArray(pivots) && pivots.length > 0;
      if (
        !selectedTeamItem ||
        !dashboardIds?.length ||
        (topicList.length === 0 && !hasPivots)
      ) {
        return false;
      }

      const metricBatches =
        topicList.length === 0
          ? [[]]
          : mode === "separate" && topicList.length > 1
          ? topicList.map((topic) => [topic])
          : [topicList];

      if (remainingKpiSlots <= 0) {
        notification.error({
          message: t("team_kpi_view.86", { max: kpiMaxLimit }),
        });
        return false;
      }

      setLoading(true);
      let created = 0;
      let lastError = null;
      let lastResult = null;
      let lastSuccess = null;
      const coveredChildren = new Set();

      for (const metrics of metricBatches) {
        const skipChildCard =
          metrics.length === 1 &&
          coveredChildren.has(String(metrics[0] ?? "").trim().toLowerCase());
        if (skipChildCard) {
          continue;
        }
        const payload = {
          dashboard_ids: dashboardIds.map((id) => parseInt(id, 10)),
          metric: metrics,
          bottom_metric: null,
          block_location: "Top",
          operation: "sum",
          team: String(selectedTeamItem),
          custom_title: "",
          ai_bubbles: [],
        };
        if (Array.isArray(pivots) && pivots.length > 0) {
          payload.pivots = pivots;
          const pivotFilter = pivotFilterFromPivots(pivots);
          if (pivotFilter) payload.pivot_filter = pivotFilter;
        }
        const result = await apiRequest(
          "executive_summary/v2/chartData",
          "POST",
          payload,
          true
        );
        if (result?.success && !result?.error) {
          created += 1;
          lastSuccess = result;
          (result.subtopic_labels || []).forEach((label) => {
            const key = String(label || "").trim().toLowerCase();
            if (key) coveredChildren.add(key);
          });
        } else {
          lastResult = result;
          lastError = result?.error || t("team_kpi_view.add_kpi_failed", "Could not add KPI");
          break;
        }
      }

      if (created > 0) {
        const subtopicNotice = formatKpiCreateSubtopicNotice(lastSuccess, t);
        notification.success({
          message:
            created > 1
              ? t("team_kpi_view.41", { count: created })
              : t("team_kpi_view.40"),
          description: subtopicNotice || undefined,
        });
        void fetchBoard().catch(() => {});
        return true;
      }

      notification.error({ message: lastError || t("team_kpi_view.add_kpi_failed", "Could not add KPI") });
      if (isKpiPaymentRequired(lastResult) && onOrgAddonUpgradeRequired) {
        onOrgAddonUpgradeRequired();
      }
      setLoading(false);
      return false;
    },
    [selectedTeamItem, fetchBoard, t, remainingKpiSlots, kpiMaxLimit, onOrgAddonUpgradeRequired]
  );

  const deleteKpi = useCallback(
    async (item) => {
      if (!item?.id) return;
      setLoading(true);
      const result = await apiRequest("executive_summary/deleteChart", "POST", {
        team: String(selectedTeamItem),
        block_location: "Top",
        id: item.id,
      });
      if (result?.success) {
        notification.success({ message: t("notifications.21") });
        await fetchBoard();
      } else {
        notification.error({ message: result?.error });
        setLoading(false);
      }
    },
    [selectedTeamItem, fetchBoard, t]
  );

  const renameKpi = useCallback(
    async (item, newName) => {
      if (!item?.id || !newName) return;
      const result = await apiRequest("executive_summary/rename", "POST", {
        team: String(selectedTeamItem),
        block_location: "Top",
        id: item.id,
        new_metric: newName,
      });
      if (result?.success) {
        notification.success({ message: t("notifications.22") });
        await fetchBoard();
      } else {
        notification.error({ message: result?.error });
      }
    },
    [selectedTeamItem, fetchBoard, t]
  );

  const moveKpi = useCallback(
    async (item, targetTeamId) => {
      if (!item?.id || !targetTeamId) return false;
      if (String(targetTeamId) === String(selectedTeamItem)) return false;
      setLoading(true);
      const result = await apiRequest("executive_summary/moveKpi", "POST", {
        id: item.id,
        source_team: String(selectedTeamItem),
        target_team: String(targetTeamId),
      });
      if (result?.success) {
        notification.success({ message: t("notifications.25") });
        await fetchBoard();
        return true;
      }
      notification.error({
        message: result?.error || t("team_kpi_view.84"),
      });
      setLoading(false);
      return false;
    },
    [selectedTeamItem, fetchBoard, t]
  );

  const editKpi = useCallback(
    async ({ item, metric, dashboardIds, pivots = [] }) => {
      if (!item?.id || !dashboardIds?.length) {
        return false;
      }
      const metrics = metric
        ? Array.isArray(metric)
          ? metric
          : [metric]
        : [];
      if (metrics.length === 0 && (!Array.isArray(pivots) || pivots.length === 0)) {
        return false;
      }

      setLoading(true);
      const payload = {
        id: item.id,
        metric: metrics,
        dashboard_ids: dashboardIds.map((id) => parseInt(id, 10)),
        operation: item.operation || "sum",
        block_location: "Top",
        pivots: Array.isArray(pivots) ? pivots : [],
      };
      const pivotFilter = pivotFilterFromPivots(payload.pivots);
      if (pivotFilter) {
        payload.pivot_filter = pivotFilter;
      } else {
        payload.pivot_filter = null;
      }

      const result = await apiRequest(
        "executive_summary/editKpi",
        "POST",
        payload,
        true
      );
      if (result?.success && !result?.error) {
        notification.success({ message: t("team_kpi_view.40") });
        await fetchBoard();
        return true;
      }
      notification.error({ message: result?.error || t("team_kpi_view.update_kpi_failed", "Could not update KPI") });
      setLoading(false);
      return false;
    },
    [fetchBoard, t]
  );

  const renameGroup = useCallback(
    async (group, name) => {
      if (!selectedTeamItem) return;
      const result = await apiRequest(
        "executive_summary/subtitles/add",
        "POST",
        {
          page: group.page,
          name,
          team: String(selectedTeamItem),
        },
        true
      );
      if (result?.success) {
        notification.success({ message: t("notifications.23") });
        await fetchBoard();
      } else if (result?.error) {
        notification.error({ message: result.error });
      }
    },
    [selectedTeamItem, fetchBoard, t]
  );

  return {
    loading,
    backgroundLoading,
    sourcesLoading,
    kpis,
    groups,
    sources,
    totalKpis,
    kpiOrgUsed,
    kpiMaxLimit,
    kpiLimitReached,
    remainingKpiSlots,
    fetchBoard,
    addKpi,
    createKpisFromTopics,
    deleteKpi,
    renameKpi,
    moveKpi,
    editKpi,
    renameGroup,
    resolveKpiTitle,
  };
}
